import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Skeleton } from 'antd';
import { setIsGooglePayAvailable } from '../../../../slices/GooglePaySlice';
import Loading from '../../loadingScreen/Loading';
import { useTranslation } from 'react-i18next';
import ErrorModal from '../../modals/errorModal/ErrorModal';
import { InfoCircleOutlined } from "@ant-design/icons";
import { isEmpty } from '../../../../Utils/utils';


const GooglePayComponent = ({
    st,
    formRef,
    jwt,
    handleInitiatePayment,
    onOrderSuccess,
    onPaymentFailed,
    showErrorModal, setShowErrorModal,
    isFormSubmitting, setIsFormSubmitting,
    clearFormAction,
    handleInitiatePaymentAnalytics,
    walletSurcharges = [],
    totalPaymentSummaryValue,
}) => {

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const initAttempted = useRef(false);
    const observerRef = useRef(null);
    const timerRef = useRef(null);

    const handleInitiatePaymentRef = useRef(handleInitiatePayment);
    useEffect(() => {
        handleInitiatePaymentRef.current = handleInitiatePayment;
    }, [handleInitiatePayment]);

    const [isButtonReady, setIsButtonReady] = useState(false);

    const sessionDetails = useSelector((state) => state.paymentSessionDetails.sessionDetails);
    const googlePayConfig = sessionDetails.configs?.paymentMethods?.find(
        (el) => el.type?.toLowerCase() === 'wallet' && el.brand?.toLowerCase() === 'googlepay'
    );
    const configData = googlePayConfig?.additionalData;

    const baseAmount = Number(sessionDetails.paymentDetails?.money?.amount);
    const surchargeTotal = walletSurcharges.reduce((sum, s) => sum + Number(s.calculatedSurchargeFee || 0), 0);
    const computedTotal = walletSurcharges.length > 0 ? baseAmount + surchargeTotal : null;
    const totalPrice = Number(computedTotal ?? totalPaymentSummaryValue ?? baseAmount ?? 0).toFixed(2);
    const countryCode = sessionDetails.paymentDetails?.context?.countryCode;
    const currency = sessionDetails.paymentDetails?.money?.currencyCode;

    const displayItems = walletSurcharges.length > 0 ? [
        {
            label: 'Subtotal',
            type: 'SUBTOTAL',
            status: 'FINAL',
            price: baseAmount.toFixed(2),
        },
        ...walletSurcharges.map((s) => ({
            label: s.surchargeDetails?.title ?? 'Surcharge',
            type: 'LINE_ITEM',
            status: 'FINAL',
            price: Number(s.calculatedSurchargeFee).toFixed(2),
        })),
    ] : undefined;

    const displayItemsRef = useRef(displayItems);
    const totalPriceRef = useRef(totalPrice);
    useEffect(() => { displayItemsRef.current = displayItems; }, [displayItems]);
    useEffect(() => { totalPriceRef.current = totalPrice; }, [totalPrice]);

    // TrustPayments SDK drops displayItems from transactionInfo, so we patch
    // loadPaymentData on the Google Pay client prototype to inject them.
    const gpPatchRef = useRef(null);

    // Reset initAttempted when st is cleared so a new instance triggers fresh init
    useEffect(() => {
        if (!st) {
            initAttempted.current = false;
        }
    }, [st]);

    const rejectPayment = (message) => Promise.resolve({
        transactionState: 'ERROR',
        error: { message: message || 'An error occurred, please try again.' }
    });

    const acceptPayment = () => Promise.resolve({ transactionState: 'SUCCESS' });

    async function onPaymentAuthorized(paymentData) {
        try {
            const result = await handleInitiatePaymentRef.current('wallet/googlepay');

            if (result?.success && result?.formActionUrl) {
                if (formRef.current) formRef.current.action = result.formActionUrl;
                return acceptPayment();
            } else {
                setIsFormSubmitting(false);

                // Retry limit is shown by the global modal in BoxPayCheckout.
                if (!result?.retryLimitReached) setShowErrorModal(true);
                handleInitiatePaymentAnalytics("GooglePay/paymentInitiateError", JSON.stringify(result));
                return rejectPayment(result?.message || "Something Went Wrong, Please Try Again");
            }
        } catch (err) {
            handleInitiatePaymentAnalytics("GooglePay/paymentInitiateError", JSON.stringify(err));
            setShowErrorModal(true);
            setIsFormSubmitting(false);
            return rejectPayment();
        }
    }

    const ALLOWED_PAYMENT_METHODS = !isEmpty(configData?.allowedPaymentMethods) ? JSON.parse(JSON.stringify(configData?.allowedPaymentMethods)) : [];

    useEffect(() => {
        if (!st || !jwt || initAttempted.current) return;

        timerRef.current = setTimeout(() => {
            const container = document.getElementById('st-google-pay');

            if (!container) return;

            initAttempted.current = true;

            try {
                st.on('paymentCompleted', (data) => {
                    setIsFormSubmitting(true);
                    onOrderSuccess?.(data);
                });

                st.on('paymentFailed', (data) => {
                    if (formRef.current) {
                        const currentAction = formRef.current.getAttribute('action');
                        if (currentAction && currentAction !== "" && currentAction !== window.location.href) {
                            const jwtInput = document.createElement('input');
                            jwtInput.type = 'hidden';
                            jwtInput.name = 'jwt';
                            jwtInput.value = jwt;
                            formRef.current.appendChild(jwtInput);
                            formRef.current.submit();
                        } else {
                            setIsFormSubmitting(false);
                        }
                    }
                });

                st.on('googlePayNotAvailable', () => {
                    dispatch(setIsGooglePayAvailable(false));
                });

                const trustGooglePayRequest = {
                    buttonOptions: {
                        buttonRootNode: 'st-google-pay',
                        buttonColor: 'black',
                        buttonType: 'plain',
                        buttonSizeMode: "fill",
                        buttonRadius: 6
                    },
                    paymentRequest: {
                        environment: process.env.REACT_APP_ENVIRONMENT === 'PRODUCTION' ? "PRODUCTION" : "TEST",
                        allowedPaymentMethods: ALLOWED_PAYMENT_METHODS,
                        merchantInfo: {
                            merchantId: configData?.merchantId,
                            merchantName: configData?.merchantName,
                        },
                        callbackIntents: ['PAYMENT_AUTHORIZATION'],
                        paymentDataCallbacks: { onPaymentAuthorized },
                        transactionInfo: {
                            totalPriceStatus: 'FINAL',
                            totalPrice: totalPriceRef.current,
                            totalPriceLabel: 'Total',
                            currencyCode: currency,
                            countryCode,
                            ...(displayItemsRef.current && { displayItems: displayItemsRef.current }),
                        },
                    },
                };

                console.log('[TrustGooglePay] st.GooglePay paymentRequest', JSON.stringify(trustGooglePayRequest.paymentRequest));
                st.GooglePay(trustGooglePayRequest);

                observerRef.current = new MutationObserver(() => {
                    if (container.children.length > 0) {
                        setIsButtonReady(true);
                        observerRef.current?.disconnect();

                        // TrustPayments strips displayItems internally — patch the prototype
                        // to inject them before the request reaches Google Pay.
                        try {
                            const gpApi = window.google?.payments?.api;
                            if (gpApi?.PaymentsClient && !gpPatchRef.current) {
                                const proto = gpApi.PaymentsClient.prototype;
                                const orig = proto.loadPaymentData;
                                gpPatchRef.current = orig;
                                proto.loadPaymentData = function (request) {
                                    try {
                                        const items = displayItemsRef.current;
                                        const price = totalPriceRef.current;
                                        if (request?.transactionInfo && items?.length) {
                                            request.transactionInfo.displayItems = items;
                                            request.transactionInfo.totalPrice = price;
                                            request.transactionInfo.totalPriceLabel = 'Total';
                                        }
                                    } catch (_) {
                                        // surcharge display failed — payment continues without line items
                                    }
                                    console.log('[TrustGooglePay] loadPaymentData request', JSON.stringify(request));
                                    return orig.call(this, request);
                                };
                            }
                        } catch (_) {
                            // patch failed — payment continues without surcharge display
                        }
                    }
                });
                observerRef.current.observe(container, { childList: true, subtree: true });

            } catch (err) {
                dispatch(setIsGooglePayAvailable(false));
            }
        }, 800);

        return () => {
            clearTimeout(timerRef.current);
            observerRef.current?.disconnect();
            if (gpPatchRef.current && window.google?.payments?.api?.PaymentsClient) {
                window.google.payments.api.PaymentsClient.prototype.loadPaymentData = gpPatchRef.current;
                gpPatchRef.current = null;
            }
        };
    }, [st, jwt, configData, totalPrice]);

    return (
        <>
            {isFormSubmitting && (
                <Loading
                    text={t("PLEASE_WAIT_PROCESSING_PAYMENT")}
                    subText={t("NOTE_AVOID_BACK_BUTTON")}
                />
            )}

            <ErrorModal
                title={t("PAYMENT_FAILED")}
                desc={t("PAYMENT_RETRY_NOTICE")}
                showModal={showErrorModal}
                primaryOnClick={() => setShowErrorModal(false)}
                icon={<InfoCircleOutlined style={{ color: "#FF4D4F" }} />}
            />

            <div className="payment-container">
                {!isButtonReady && (
                    <Skeleton.Button active style={{ width: "100%", height: 40 }} shape="default" />
                )}
                <div style={{ display: isButtonReady ? 'block' : 'none' }}>
                    <div id="st-google-pay" onClick={() => {
                        setIsFormSubmitting(true)
                        clearFormAction()
                        handleInitiatePaymentAnalytics("GooglePay")
                    }} className='payment-button-box' />
                </div>
            </div>
        </>
    );
};

export default GooglePayComponent;
